import NewWindowSvg from 'assets/images/new-window.svg'
import I18n from 'components/I18n'
import Popover from 'components/Popover'
import { actions, selectors } from 'data'
import { UserType } from 'data/types'
import messages from 'lang'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { getWalletLoginUrl } from 'utils'

import { showDepositModal, showWithdrawalModal } from '../../menuActions'
import { Menu, MenuItem, MenuItemSeperator, MenuLink, OutsideLink, renderTrigger } from './common'

type PropsType = {
  hasMoney: boolean
  user: UserType
  walletGuid?: string
  walletUrl: string
}

const MobileSignedIn = ({ hasMoney, user, walletGuid, walletUrl }: PropsType) => {
  const [isActive, setActive] = React.useState<boolean>(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isSilver = useSelector(selectors.auth.isUserSilverishTier)

  const redirectToSettings = () => navigate('/settings')
  const redirectToHistory = () => navigate('/history')
  const redirectToMarkets = () => navigate('/market')

  // Utility function that closes the popover and then calls passed FX
  const closeAnd = (fx) => () => {
    setActive(false)
    return fx(dispatch)
  }

  return (
    <Popover
      active={isActive}
      e2e='navigatePopover'
      onVisibleToggle={() => setActive(!isActive)}
      renderTrigger={renderTrigger}
    >
      <Menu>
        {hasMoney && (
          <MenuItem>
            <MenuLink
              data-e2e='withdrawButton'
              onClick={closeAnd(showWithdrawalModal)}
              weight={600}
            >
              <I18n data={messages.button_withdraw} />
            </MenuLink>
          </MenuItem>
        )}

        {isSilver ? (
          <MenuItem>
            <MenuLink
              data-e2e='upgradeButton'
              onClick={closeAnd(() => dispatch(actions.app.showModal('TIER_TWO')))}
              weight={600}
            >
              <I18n data={messages.upgrade_to_gold} />
            </MenuLink>
          </MenuItem>
        ) : (
          <MenuItem>
            <MenuLink
              data-e2e='depositButton'
              onClick={closeAnd(() => showDepositModal(dispatch))}
              weight={600}
            >
              <I18n data={messages.button_deposit} />
            </MenuLink>
          </MenuItem>
        )}

        <MenuItemSeperator />

        <MenuItem>
          <MenuLink data-e2e='goToMarketsPage' onClick={redirectToMarkets} weight={600}>
            <I18n data={messages.markets} />
          </MenuLink>
        </MenuItem>

        <MenuItem>
          <MenuLink
            data-e2e='goToTradePage'
            onClick={() => {
              dispatch(
                actions.analytics.trackEvent({
                  key: 'Trading Clicked',
                  properties: {
                    origin: 'NAVIGATION'
                  }
                })
              )
              navigate('/trade')
            }}
            weight={600}
          >
            <I18n data={messages.trade} />
          </MenuLink>
        </MenuItem>

        <MenuItem>
          <MenuLink
            data-e2e='goToPorfolioPage'
            onClick={() => {
              dispatch(
                actions.analytics.trackEvent({
                  key: 'Portfolio Clicked',
                  properties: {
                    origin: 'NAVIGATION'
                  }
                })
              )
              navigate('/portfolio')
            }}
            weight={600}
          >
            <I18n data={messages.portfolio} />
          </MenuLink>
        </MenuItem>

        <MenuItem>
          <MenuLink data-e2e='goToSettingsPage' onClick={closeAnd(redirectToSettings)} weight={600}>
            <I18n data={messages.settings} />
          </MenuLink>
        </MenuItem>

        <MenuItem>
          <MenuLink
            data-e2e='goToHistoryPage'
            onClick={() => {
              dispatch(
                actions.analytics.trackEvent({
                  key: 'History Clicked',
                  properties: {
                    origin: 'NAVIGATION',
                    type: 'TRADE'
                  }
                })
              )
              closeAnd(redirectToHistory)()
            }}
            weight={600}
          >
            <I18n data={messages.history} />
          </MenuLink>
        </MenuItem>

        {walletGuid ? (
          <MenuItem>
            <MenuLink
              data-e2e='linkWallet'
              href={getWalletLoginUrl(walletUrl, walletGuid, user.email)}
              target='_blank'
              weight={600}
            >
              <I18n data={messages.go_to_wallet} />
              &nbsp;
              <OutsideLink src={NewWindowSvg} />
            </MenuLink>
          </MenuItem>
        ) : null}

        <MenuItemSeperator />

        <MenuItem>
          <MenuLink
            data-e2e='headerSignout'
            onClick={() => {
              dispatch(
                actions.analytics.trackEvent({
                  key: 'Signed Out',
                  properties: { origin: 'NAVIGATION' }
                })
              )
              navigate('/trade')
              dispatch(actions.auth.signout())
            }}
          >
            <I18n data={messages.button_signout} />
          </MenuLink>
        </MenuItem>
      </Menu>
    </Popover>
  )
}

export default MobileSignedIn
